import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  IconButton,
  Popover,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import { EmojiEmotionsOutlined as EmojiIcon } from '@mui/icons-material'
import { LazyEmojiPicker } from '@/components/common/LazyEmojiPicker'
import { chatApi } from '@/api/chat'
import type { ChatStickerPack } from '@/api/chat'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'

const PANEL_WIDTH = 350
const PANEL_HEIGHT = 420
/** Задержка закрытия при уходе курсора — чтобы успеть переехать в панель. */
const HOVER_CLOSE_DELAY_MS = 250
/** Задержка открытия по hover — случайный пролёт курсора не открывает панель. */
const HOVER_OPEN_DELAY_MS = 200

type PanelTab = 'emoji' | 'stickers'

interface EmojiStickerPanelProps {
  disabled: boolean
  /** Комната-контекст: каталог стикеров фильтруется по её тарифу. */
  roomId: number | null
  onPickEmoji: (emoji: string) => void
  onSendSticker: (stickerId: number) => void
}

/**
 * Кнопка у поля ввода: панель открывается по наведению (как в VK/Telegram),
 * внизу — текстовый переключатель «Смайлики / Стикеры». Emoji вставляются
 * в текст (панель остаётся открытой), стикер отправляется сразу.
 */
export function EmojiStickerPanel({
  disabled,
  roomId,
  onPickEmoji,
  onSendSticker,
}: EmojiStickerPanelProps) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  // Панель, открытая по hover, прозрачна для мыши (закрывается уходом
  // курсора); открытая кликом/тапом — обычная, закрывается тапом снаружи
  // (touch-устройства mouseleave не шлют).
  const [openedByHover, setOpenedByHover] = useState(false)
  const [tab, setTab] = useState<PanelTab>('emoji')
  const closeTimerRef = useRef<number | null>(null)
  const openTimerRef = useRef<number | null>(null)

  const [packs, setPacks] = useState<ChatStickerPack[] | null>(null)
  const [isLoadingPacks, setIsLoadingPacks] = useState(false)
  const [packsError, setPacksError] = useState<string | null>(null)
  const [activePackIndex, setActivePackIndex] = useState(0)

  const cancelClose = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const cancelOpen = () => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
  }

  // Смена комнаты — другой тарифный контекст: каталог перезагружается.
  useEffect(() => {
    setPacks(null)
    setPacksError(null)
    setActivePackIndex(0)
  }, [roomId])

  // Отложенные таймеры не должны переживать размонтирование (смена комнаты).
  useEffect(
    () => () => {
      cancelClose()
      cancelOpen()
    },
    []
  )

  const scheduleClose = () => {
    cancelOpen()
    cancelClose()
    closeTimerRef.current = window.setTimeout(() => setAnchorEl(null), HOVER_CLOSE_DELAY_MS)
  }

  const scheduleOpen = (element: HTMLElement) => {
    if (disabled) return
    cancelClose()
    if (anchorEl !== null || openTimerRef.current !== null) return
    openTimerRef.current = window.setTimeout(() => {
      openTimerRef.current = null
      setOpenedByHover(true)
      setAnchorEl(element)
    }, HOVER_OPEN_DELAY_MS)
  }

  const toggleByClick = (element: HTMLElement) => {
    if (disabled) return
    cancelOpen()
    cancelClose()
    if (anchorEl !== null && !openedByHover) {
      setAnchorEl(null)
      return
    }
    setOpenedByHover(false)
    setAnchorEl(element)
  }

  const loadPacks = async (force = false) => {
    if ((packs !== null && !force) || isLoadingPacks) return
    setIsLoadingPacks(true)
    setPacksError(null)
    try {
      const loaded = await chatApi.getStickerPacks(roomId ?? undefined)
      setPacks(loaded.filter((pack) => (pack.stickers?.length ?? 0) > 0))
    } catch (error: unknown) {
      setPacksError(displayErrorMessage(getErrorMessage(error), t('common.error')))
    } finally {
      setIsLoadingPacks(false)
    }
  }

  const handleTabChange = (nextTab: PanelTab) => {
    setTab(nextTab)
    if (nextTab === 'stickers') void loadPacks()
  }

  const handleStickerPick = (stickerId: number) => {
    cancelClose()
    setAnchorEl(null)
    onSendSticker(stickerId)
  }

  const activePack = packs?.[Math.min(activePackIndex, Math.max((packs?.length ?? 1) - 1, 0))]

  return (
    <>
      <Tooltip title={t('chat.emojiButton')}>
        <span>
          <IconButton
            aria-label={t('chat.emojiButton')}
            disabled={disabled}
            onMouseEnter={(event) => scheduleOpen(event.currentTarget)}
            onMouseLeave={scheduleClose}
            onClick={(event) => toggleByClick(event.currentTarget)}
          >
            <EmojiIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        disableRestoreFocus
        disableEnforceFocus
        // Hover-режим: сам Popover прозрачен для мыши (иначе его невидимый
        // backdrop мгновенно уводит курсор с кнопки), интерактивна только
        // бумага; закрытие — по уходу курсора. Клик/тап-режим: обычный
        // Popover — тап по невидимому backdrop закрывает панель (onClose).
        sx={openedByHover ? { pointerEvents: 'none' } : undefined}
        slotProps={{
          paper: {
            onMouseEnter: cancelClose,
            onMouseLeave: openedByHover ? scheduleClose : undefined,
            sx: { pointerEvents: 'auto' },
          },
        }}
      >
        <Box sx={{ width: PANEL_WIDTH, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ height: PANEL_HEIGHT, overflow: 'hidden' }}>
            {tab === 'emoji' ? (
              <LazyEmojiPicker onPick={onPickEmoji} width={PANEL_WIDTH} height={PANEL_HEIGHT} />
            ) : (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {packsError ? (
                  <Box sx={{ p: 1.5 }}>
                    <Alert
                      severity="error"
                      action={
                        <Button color="inherit" size="small" onClick={() => void loadPacks(true)}>
                          {t('common.retry')}
                        </Button>
                      }
                    >
                      {packsError}
                    </Alert>
                  </Box>
                ) : isLoadingPacks || packs === null ? (
                  <Box sx={{ p: 1.5 }}>
                    <Skeleton height={36} sx={{ mb: 1 }} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                        <Skeleton key={i} variant="rounded" height={64} />
                      ))}
                    </Box>
                  </Box>
                ) : packs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {t('chat.noStickers')}
                  </Typography>
                ) : (
                  <>
                    {packs.length > 1 && (
                      <Tabs
                        value={Math.min(activePackIndex, packs.length - 1)}
                        onChange={(_, value: number) => setActivePackIndex(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
                      >
                        {packs.map((pack) => (
                          <Tab key={pack.id} label={pack.title} sx={{ minHeight: 36, py: 0.5 }} />
                        ))}
                      </Tabs>
                    )}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 1,
                        p: 1.5,
                        overflow: 'auto',
                        flex: 1,
                        alignContent: 'start',
                      }}
                    >
                      {(activePack?.stickers ?? []).map((sticker) => (
                        <IconButton
                          key={sticker.id}
                          onClick={() => handleStickerPick(sticker.id)}
                          sx={{ borderRadius: 1, p: 0.5 }}
                          aria-label={sticker.emoji ?? sticker.code}
                        >
                          <img
                            src={sticker.url}
                            alt={sticker.emoji ?? sticker.code}
                            loading="lazy"
                            style={{ width: 64, height: 64, objectFit: 'contain' }}
                          />
                        </IconButton>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Button
              fullWidth
              size="small"
              onClick={() => handleTabChange('emoji')}
              sx={{
                textTransform: 'none',
                borderRadius: 0,
                py: 1,
                fontWeight: tab === 'emoji' ? 700 : 400,
                color: tab === 'emoji' ? 'primary.main' : 'text.secondary',
              }}
            >
              {t('chat.emojiButton')}
            </Button>
            <Button
              fullWidth
              size="small"
              onClick={() => handleTabChange('stickers')}
              sx={{
                textTransform: 'none',
                borderRadius: 0,
                py: 1,
                fontWeight: tab === 'stickers' ? 700 : 400,
                color: tab === 'stickers' ? 'primary.main' : 'text.secondary',
              }}
            >
              {t('chat.stickers')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  )
}
